// useTeamViewTaskActions: Task action handlers for TeamView
// Manages modal state and handlers for reassign, delete, reschedule, drag-and-drop
// Extracted from TeamView.tsx to reduce component complexity
// UPDATED: Uses rollbackRef instead of window.__dayTimelineRollback for proper React data flow
// UPDATED: updateTaskStatus and updateTaskItemTextInputAsAdmin now throw on error (no more Result objects)
// CHANGE: Removed all debug console statements for production
// UPDATED: Added handleQuickStatusChange and handleQuickTextInputBatch for direct actions from TaskDetailModal
// UPDATED: Auto-syncs selectedTaskForReassign when tasks prop updates (after reloadTasks)
// UPDATED: handleTextInputChange now passes logContext so individual field saves emit audit logs
// UPDATED: handleTaskClick falls back to the passed task when not found in tasks array (list view support)

import { useState, useRef, useEffect } from 'react';
import {
  AssignedTaskWithDetails,
  reassignTask,
  rescheduleTask,
  deleteTask,
  ReassignScope,
  DeleteScope,
} from '../lib/execution';
import { getFullName } from '../lib/supabase';
import { useToast } from './useToast';
import type { Profile } from './useTeamViewData';
import type { Service } from '../lib/services';

interface UseTeamViewTaskActionsParams {
  user: { id: string; role: string } | null;
  tasks: AssignedTaskWithDetails[];
  allProfilesForSite: Profile[];
  services: Service[];
  reloadTasks: () => Promise<void>;
}

export function useTeamViewTaskActions({
  user,
  tasks,
  allProfilesForSite,
  services,
  reloadTasks,
}: UseTeamViewTaskActionsParams) {
  const [selectedTaskForReassign, setSelectedTaskForReassign] = useState<AssignedTaskWithDetails | null>(null);
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTaskForDelete, setSelectedTaskForDelete] = useState<AssignedTaskWithDetails | null>(null);
  const [dragTargetOperatorId, setDragTargetOperatorId] = useState<string | null>(null);
  const [dragTargetTime, setDragTargetTime] = useState<string | null>(null);
  const rollbackRef = useRef<(() => void) | null>(null);
  const { showToast } = useToast();

  // Auto-sync selectedTaskForReassign when tasks refresh (e.g. after reloadTasks)
  // This ensures the TaskDetailModal always displays the latest data without closing
  useEffect(() => {
    if (!selectedTaskForReassign || tasks.length === 0) return;
    const updated = tasks.find(t => t.id === selectedTaskForReassign.id);
    if (updated) {
      setSelectedTaskForReassign(updated);
    }
  }, [tasks]);

  const handleTaskClick = (timelineTask: any) => {
    const fullTask = tasks.find(t => t.id === timelineTask.id);
    if (fullTask) {
      setSelectedTaskForReassign(fullTask);
      setShowTaskDetailModal(true);
    } else if (timelineTask?.id && timelineTask?.template) {
      setSelectedTaskForReassign(timelineTask as AssignedTaskWithDetails);
      setShowTaskDetailModal(true);
    }
  };

  const handleEditFromDetail = () => {
    setShowTaskDetailModal(false);
    setShowEditModal(true);
  };

  const handleDeleteFromDetail = () => {
    setShowTaskDetailModal(false);
    setShowDeleteModal(true);
  };

  const handleStatusChange = async (newStatus: 'not_started' | 'completed' | 'overdue') => {
    if (!selectedTaskForReassign) return;
    try {
      const { updateTaskStatus } = await import('../lib/execution');
      const taskContext = selectedTaskForReassign.isVirtualOccurrence ? {
        isVirtual: true,
        parentTaskId: selectedTaskForReassign.parentTaskId,
        occurrenceDate: selectedTaskForReassign.occurrenceDate,
      } : undefined;

      await updateTaskStatus(selectedTaskForReassign.id, newStatus, taskContext, true);
    } catch (error) {
      showToast('Erreur lors de la mise à jour du statut', 'error');
      throw error;
    }
  };

  const handleTextInputChange = async (itemId: string, value: string) => {
    if (!selectedTaskForReassign) return;
    try {
      const { updateTaskItemTextInputAsAdmin } = await import('../lib/execution');
      const taskContext = selectedTaskForReassign.isVirtualOccurrence ? {
        isVirtual: true,
        parentTaskId: selectedTaskForReassign.parentTaskId,
        occurrenceDate: selectedTaskForReassign.occurrenceDate,
      } : undefined;

      const logTaskId = selectedTaskForReassign.isVirtualOccurrence
        ? selectedTaskForReassign.parentTaskId
        : selectedTaskForReassign.id;

      await updateTaskItemTextInputAsAdmin(itemId, value, taskContext, { taskId: logTaskId ?? undefined });
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Erreur lors de la mise à jour du champ', 'error');
      throw error;
    }
  };

  // Quick status change directly from TaskDetailModal — always single-occurrence, no scope question
  const handleQuickStatusChange = async (newStatus: 'not_started' | 'completed' | 'overdue') => {
    await handleStatusChange(newStatus);
    await reloadTasks();
    showToast('Statut mis à jour', 'success');
  };

  // Quick batch text input save directly from TaskDetailModal — always single-occurrence
  const handleQuickTextInputBatch = async (values: Record<string, string>) => {
    if (!selectedTaskForReassign) return;
    const { updateTaskItemTextInputAsAdmin } = await import('../lib/execution');
    const taskContext = selectedTaskForReassign.isVirtualOccurrence ? {
      isVirtual: true,
      parentTaskId: selectedTaskForReassign.parentTaskId,
      occurrenceDate: selectedTaskForReassign.occurrenceDate,
    } : undefined;

    const logTaskId = selectedTaskForReassign.isVirtualOccurrence
      ? selectedTaskForReassign.parentTaskId
      : selectedTaskForReassign.id;
    await Promise.all(
      Object.entries(values).map(([itemId, value]) =>
        updateTaskItemTextInputAsAdmin(itemId, value, taskContext, { taskId: logTaskId ?? undefined })
      )
    );
    await reloadTasks();
    showToast('Champs mis à jour', 'success');
  };

  const handleSaveComplete = async () => {
    showToast('Mission mise à jour', 'success');
    await reloadTasks();
    setShowEditModal(false);
  };

  const handleTaskDropErrorCallback = () => {
    rollbackRef.current?.();
  };

  const handleReassignTaskDirect = async (task: AssignedTaskWithDetails, newUserId: string, newScheduledTime?: string) => {
    if (!user) return;
    try {
      await reassignTask({
        taskId: task.id,
        newUserId,
        scope: 'single',
        currentUserId: user.id,
        isVirtualOccurrence: task.isVirtualOccurrence,
        parentTaskId: task.parentTaskId,
        occurrenceDate: task.occurrenceDate,
        newScheduledTime,
      });

      const operator = allProfilesForSite.find(p => p.id === newUserId);
      const operatorName = operator ? getFullName(operator) : "l'opérateur";
      const timeMessage = newScheduledTime ? ` à ${newScheduledTime}` : '';
      showToast(`Mission réattribuée à ${operatorName}${timeMessage}`, 'success');
      await reloadTasks();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Erreur lors de la réattribution', 'error');
      handleTaskDropErrorCallback();
    }
  };

  const handleRescheduleTaskDirect = async (task: AssignedTaskWithDetails, newScheduledTime: string) => {
    if (!user) return;
    try {
      await rescheduleTask({
        taskId: task.id,
        newScheduledTime,
        currentUserId: user.id,
        isVirtualOccurrence: task.isVirtualOccurrence,
        parentTaskId: task.parentTaskId,
        occurrenceDate: task.occurrenceDate,
      });
      showToast(`Mission replanifiée à ${newScheduledTime}`, 'success');
      await reloadTasks();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Erreur lors de la replanification', 'error');
      handleTaskDropErrorCallback();
    }
  };

  const handleTaskDrop = (taskId: string, fromOperatorId: string, toOperatorId: string, timelineTask: any, newScheduledTime: string) => {
    const fullTask = tasks.find(t => t.id === taskId);
    if (!fullTask) return;

    const operatorChanged = fromOperatorId !== toOperatorId;
    const timeChanged = fullTask.scheduled_time !== newScheduledTime;

    if (!operatorChanged && !timeChanged) return;

    if (!operatorChanged && timeChanged) {
      if (fullTask.is_recurring) {
        setDragTargetOperatorId(null);
        setDragTargetTime(newScheduledTime);
        setSelectedTaskForReassign(fullTask);
        setShowEditModal(true);
      } else {
        handleRescheduleTaskDirect(fullTask, newScheduledTime);
      }
    } else if (operatorChanged) {
      if (fullTask.is_recurring) {
        setDragTargetOperatorId(toOperatorId);
        setDragTargetTime(timeChanged ? newScheduledTime : null);
        setSelectedTaskForReassign(fullTask);
        setShowEditModal(true);
      } else {
        handleReassignTaskDirect(fullTask, toOperatorId, newScheduledTime);
      }
    }
  };

  const handleReassignTask = async (
    newUserId: string | null,
    scope: ReassignScope,
    untilDate?: string,
    newScheduledTime?: string,
    newScheduledDate?: string
  ) => {
    if (!selectedTaskForReassign || !user) return;

    try {
      const operatorChanged = newUserId && newUserId !== selectedTaskForReassign.assigned_to_user_id;
      const timeChanged = newScheduledTime && newScheduledTime !== selectedTaskForReassign.scheduled_time;
      const dateChanged = newScheduledDate && newScheduledDate !== selectedTaskForReassign.assigned_date;

      if (!operatorChanged && (timeChanged || dateChanged)) {
        await rescheduleTask({
          taskId: selectedTaskForReassign.id,
          newScheduledTime: newScheduledTime,
          currentUserId: user.id,
          isVirtualOccurrence: selectedTaskForReassign.isVirtualOccurrence,
          parentTaskId: selectedTaskForReassign.parentTaskId,
          occurrenceDate: selectedTaskForReassign.occurrenceDate,
        });

        const changes: string[] = [];
        if (dateChanged) changes.push(`date modifiée`);
        if (timeChanged) changes.push(`heure: ${newScheduledTime}`);
        showToast(`Mission replanifiée (${changes.join(', ')})`, 'success');
      } else if (operatorChanged) {
        await reassignTask({
          taskId: selectedTaskForReassign.id,
          newUserId: newUserId,
          scope,
          untilDate,
          currentUserId: user.id,
          isVirtualOccurrence: selectedTaskForReassign.isVirtualOccurrence,
          parentTaskId: selectedTaskForReassign.parentTaskId,
          occurrenceDate: selectedTaskForReassign.occurrenceDate,
          newScheduledTime,
        });

        const operator = allProfilesForSite.find(p => p.id === newUserId);
        const operatorName = operator ? getFullName(operator) : "l'opérateur";
        const changes: string[] = [];
        if (timeChanged) changes.push(`heure: ${newScheduledTime}`);
        if (dateChanged) changes.push(`date modifiée`);
        const extraMessage = changes.length > 0 ? ` (${changes.join(', ')})` : '';
        showToast(`Mission réattribuée à ${operatorName}${extraMessage}`, 'success');
      }

      await reloadTasks();
      setShowEditModal(false);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Erreur lors de la modification', 'error');
      throw error;
    }
  };

  const handleDeleteTask = async (scope: DeleteScope, untilDate?: string) => {
    if (!selectedTaskForReassign || !user) return;

    try {
      await deleteTask({
        taskId: selectedTaskForReassign.id,
        scope,
        untilDate,
        currentUserId: user.id,
        isVirtualOccurrence: selectedTaskForReassign.isVirtualOccurrence,
        parentTaskId: selectedTaskForReassign.parentTaskId,
        occurrenceDate: selectedTaskForReassign.occurrenceDate,
      });

      showToast('Mission supprimée', 'success');
      await reloadTasks();
      setShowDeleteModal(false);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Erreur lors de la suppression', 'error');
      throw error;
    }
  };

  return {
    selectedTaskForReassign,
    showTaskDetailModal,
    showEditModal,
    showDeleteModal,
    selectedTaskForDelete,
    dragTargetOperatorId,
    dragTargetTime,
    setSelectedTaskForReassign,
    setShowTaskDetailModal,
    setShowEditModal,
    setShowDeleteModal,
    setSelectedTaskForDelete,
    setDragTargetOperatorId,
    setDragTargetTime,
    rollbackRef,
    handleTaskClick,
    handleEditFromDetail,
    handleDeleteFromDetail,
    handleStatusChange,
    handleTextInputChange,
    handleQuickStatusChange,
    handleQuickTextInputBatch,
    handleSaveComplete,
    handleTaskDrop,
    handleTaskDropErrorCallback,
    handleReassignTask,
    handleDeleteTask,
  };
}
